/* App bootstrap: builds the shell frame (topbar + content + tab bar), runs the
 * hash router over the surface registry, and initializes native push. */
import './styles/tokens.css';
import './styles/app.css';
import { SURFACES, DEFAULT_SURFACE, findSurface, type Surface } from './surfaces';
import { getStore } from './store';
import { icon } from './components/icons';
import { esc, surfaceTitle } from './components/ui';
import { initPush } from './native/push';

const app = document.getElementById('app')!;

app.innerHTML = `
  <header class="topbar">
    <h1 id="ttl">Skippy</h1>
    <span class="brain-stamp" id="brainstamp">brain: —</span>
  </header>
  <main class="content" id="content"></main>
  <nav class="tabbar" id="tabbar"></nav>
`;

const content = document.getElementById('content')!;
const titleEl = document.getElementById('ttl')!;
const tabbar = document.getElementById('tabbar')!;

/* Bottom tab bar: the primary surfaces + a "More" button. */
function paintTabbar(activeId: string): void {
  const primaries = SURFACES.filter((s) => s.primary);
  const buttons = primaries
    .map(
      (s) =>
        `<button data-go="${s.id}" ${s.id === activeId ? 'aria-current="page"' : ''}>${icon(s.icon)}<span>${esc(
          s.title,
        )}</span></button>`,
    )
    .join('');
  const moreActive = !primaries.some((s) => s.id === activeId);
  tabbar.innerHTML =
    buttons +
    `<button data-go="more" ${moreActive ? 'aria-current="page"' : ''}>${icon('more')}<span>More</span></button>`;
}

/* The "More" overflow surface lists every non-primary surface. */
function renderMore(root: HTMLElement): void {
  const rest = SURFACES.filter((s) => !s.primary);
  root.innerHTML =
    surfaceTitle('More') +
    `<div class="more-grid">${rest
      .map((s) => `<button class="btn ghost" data-go="${s.id}">${icon(s.icon)}<span>${esc(s.title)}</span></button>`)
      .join('')}</div>`;
}

async function route(): Promise<void> {
  const id = location.hash.replace(/^#\/?/, '') || DEFAULT_SURFACE;
  content.onclick = null;
  content.innerHTML = '';

  if (id === 'more') {
    titleEl.textContent = 'More';
    renderMore(content);
    paintTabbar('more');
    return;
  }

  const surface: Surface = findSurface(id);
  titleEl.textContent = surface.title;
  paintTabbar(surface.id);
  try {
    await surface.render(content);
  } catch (err) {
    content.innerHTML = `<div class="softfail">Failed to load ${esc(surface.title)}: ${esc(String(err))}</div>`;
  }
}

/* Nav clicks (tab bar + "More" grid) → hash change. */
document.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-go]');
  if (!btn) return;
  location.hash = `#/${btn.dataset.go}`;
});

window.addEventListener('hashchange', () => void route());

/* Brain-freshness stamp in the topbar (spec §4: stale-brain guard is a UI state). */
async function paintBrainStamp(): Promise<void> {
  const s = await getStore().getSettings();
  const el = document.getElementById('brainstamp')!;
  const gen = new Date(s.data.brainGeneratedAt);
  if (isNaN(gen.getTime())) {
    // No valid brain timestamp yet — show a neutral dash, never "Invalid Date".
    el.textContent = 'brain: —';
    delete el.dataset.stale;
    return;
  }
  const ageH = (Date.now() - gen.getTime()) / 3.6e6;
  el.textContent = `brain: ${gen.toLocaleTimeString()}`;
  el.dataset.stale = String(ageH > 6); // >6h warn (spec §0.6)
}

async function boot(): Promise<void> {
  await getStore().getIdentity(); // session/persona (signed by engine in prod)
  await paintBrainStamp();
  await route();
  // Native push registers only on a real device; no-ops in the browser build. On a
  // device, the registration callback POSTs the token to the bridge (/api/push/register
  // via the store client). Best-effort: a failed register never blocks boot, and the
  // Mock client no-ops it, so this is inert until a real device + live bridge.
  void initPush(async (token, platform) => {
    try {
      await getStore().registerPush(token, platform);
    } catch {
      /* best-effort — never block boot on a push-token round-trip */
    }
  });
}

void boot();
